import { ThemeContext } from '@f_context/Theme_context';
import { UserContext } from '@f_context/User_context';
import stls from '@f_qwep/styles/component/info/Info.module.sass';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import {
    Alert,
    Box,
    Button,
    CardMedia,
    Checkbox,
    CircularProgress,
    Divider,
    FormControlLabel,
    Grid,
    Modal,
    TextField,
    Typography,
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useMigrationContext } from './context/Migration_context';
import { MigrationValidator, ValidationErrors } from './MigrationValidation';
import { PhoneInput } from './PhoneInput';

type TUser = {
    email: string;
    firstName: string;
    lastName: string;
};

export const MigrationModal = () => {
    const { colors } = useContext(ThemeContext);
    const { openModal, setOpenModal, validateAccount, loading, info, migrationAccount } = useMigrationContext();
    const { userInfo } = useContext(UserContext);

    const [email, setEmail] = useState<string>(userInfo?.email ?? '');
    const [showEditForm, setShowEditForm] = useState<boolean>(false);
    const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
    const [editData, setEditData] = useState({
        firstName: '',
        lastName: '',
        position: '',
        workspaceName: '',
        phoneNumber: '',
        password: '',
        passwordConfirm: '',
        users: [] as TUser[],
        key: '',
    });
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [errors, setErrors] = useState<ValidationErrors>({});
    const [emailError, setEmailError] = useState<string>('');

    useEffect(() => {
        if (userInfo) {
            setEmail(userInfo?.email ?? '');
        }
    }, [userInfo]);

    useEffect(() => {
        if (info) {
            setEditData({
                firstName: info.firstName || '',
                lastName: info.lastName || '',
                position: info.position || '',
                workspaceName: info.workspaceName || '',
                phoneNumber: '',
                password: '',
                passwordConfirm: '',
                users: info.users || [],
                key: '',
            });
            // По умолчанию выделяем всех пользователей
            setSelectedUsers(info.users ? info.users.map((user) => user.email) : []);
            setShowEditForm(true);
            setErrors({});
        }
    }, [info]);

    const handleClose = () => {
        setOpenModal(false);
        setShowEditForm(false);
        setShowSuccessScreen(false);
        setEditData({
            firstName: '',
            lastName: '',
            position: '',
            workspaceName: '',
            phoneNumber: '',
            password: '',
            passwordConfirm: '',
            users: [],
            key: '',
        });
        setSelectedUsers([]);
        setErrors({});
        setEmailError('');
    };

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email || email.trim() === '') {
            setEmailError('Email обязателен для заполнения');
            return false;
        }
        if (!emailRegex.test(email)) {
            setEmailError('Введите корректный email адрес');
            return false;
        }
        setEmailError('');
        return true;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!validateEmail(email)) {
            return;
        }

        await validateAccount(email);
    };

    const validateForm = (): boolean => {
        const validationData = {
            ...editData,
            users: selectedUsers,
        };

        const validationErrors = MigrationValidator.validateForm(validationData);
        setErrors(validationErrors);

        return Object.keys(validationErrors).length === 0;
    };

    const handleEditSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            await migrationAccount(editData.key, {
                firstName: editData.firstName,
                lastName: editData.lastName,
                position: editData.position,
                phoneNumber: editData.phoneNumber,
                workspaceName: editData.workspaceName,
                password: editData.password,
                users: selectedUsers ?? null,
            });
            setShowSuccessScreen(true);
        } catch (error) {
            console.error('Ошибка миграции:', error);
        }
    };

    const handleInputChange = (field: string, value: string) => {
        setEditData((prev) => ({
            ...prev,
            [field]: value,
        }));

        if (errors[field as keyof ValidationErrors]) {
            setErrors((prev) => ({
                ...prev,
                [field]: undefined,
            }));
        }
    };

    const handleUserSelection = (userEmail: string, checked: boolean) => {
        if (checked) {
            setSelectedUsers((prev) => [...prev, userEmail]);
        } else {
            setSelectedUsers((prev) => prev.filter((email) => email !== userEmail));
        }

        if (errors.users) {
            setErrors((prev) => ({
                ...prev,
                users: undefined,
            }));
        }
    };

    const handleSelectAllUsers = (checked: boolean) => {
        if (checked) {
            setSelectedUsers(editData.users.map((user) => user.email));
        } else {
            setSelectedUsers([]);
        }

        if (errors.users) {
            setErrors((prev) => ({
                ...prev,
                users: undefined,
            }));
        }
    };

    const reloadPage = () => {
        window.location.href = 'https://web.qwep.ru/';
    };

    const renderValidationForm = () => (
        <>
            <Typography id="modal-modal-title" variant="h6" component="h2">
                Миграция на новую версию
            </Typography>
            <form style={{ display: 'flex', flexDirection: 'column', gap: 16 }} onSubmit={handleSubmit}>
                <TextField
                    sx={{ mt: 1 }}
                    placeholder="Введите Email"
                    value={email}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        if (emailError) {
                            setEmailError('');
                        }
                    }}
                    error={!!emailError}
                    helperText={emailError}
                />

                <Typography id="modal-modal-description" sx={{ marginBottom: 1 }}>
                    Вы можете изменить Email для миграции на новую версию.
                    <br />
                    Для этого введите новый адрес и нажмите кнопку «Проверить доступ».
                </Typography>
                <Typography id="modal-modal-description" sx={{ marginBottom: 1 }}>
                    На указанную почту при проверке придет уникальный ключ для миграции.
                    <br />
                    обязательно укажите его на след. шаге.
                </Typography>
                <Alert
                    severity="warning"
                    icon={<WarningIcon sx={{ color: '#df4949' }} />}
                    sx={{
                        marginBottom: 2,
                        '& .MuiAlert-message': {
                            '& a': {
                                color: 'inherit',
                                textDecoration: 'underline',
                                fontWeight: 'bold',
                            },
                        },
                    }}>
                    <strong>Важно!</strong> После завершения миграции, вам будет доступен только новый аккаунт. Вы
                    можете войти в него по адресу{' '}
                    <a href="https://web.qwep.ru/" target="_blank" rel="noopener noreferrer">
                        web.qwep.ru
                    </a>
                </Alert>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <CardMedia
                        className={stls.migrationMedia}
                        component="iframe"
                        allow={'fullscreen'}
                        image={
                            'https://rutube.ru/play/embed/611cc382bb4a6b77b581db1834765507/?p=KuYmIurNo5WaYZUx3E26Tw'
                        }
                        src={'https://rutube.ru/play/embed/611cc382bb4a6b77b581db1834765507/?p=KuYmIurNo5WaYZUx3E26Tw'}
                    />

                    <CardMedia
                        className={stls.migrationMedia}
                        component="iframe"
                        allow={'fullscreen'}
                        image={'https://vk.com/video_ext.php?oid=-148523309&id=456239094&hd=4&hash=14b2654137675e1b'}
                        src={'https://vk.com/video_ext.php?oid=-148523309&id=456239094&hd=4&hash=14b2654137675e1b'}
                    />
                </div>
                {/*<Button*/}
                {/*    variant="contained"*/}
                {/*    style={{ backgroundColor: '#6EBA6E', display: 'flex', flexShrink: 0 }}*/}
                {/*    href={videoContent.assistant.rutube}*/}
                {/*    target="_blank">*/}
                {/*    <YouTube style={{ color: 'white' }} /> {'\u00A0'} Обучающее видео*/}
                {/*</Button>*/}
                <Button variant="contained" color="primary" type="submit" disabled={loading}>
                    {loading ? (
                        <CircularProgress size={25} sx={{ color: '#fff', margin: '2px 0' }} />
                    ) : (
                        <>
                            Проверить доступ
                            <ArrowForwardIcon sx={{ ml: 1, fontSize: 18, color: '#fff' }} />
                        </>
                    )}
                </Button>
            </form>
        </>
    );

    const renderEditForm = () => (
        <>
            <Typography id="modal-modal-title" variant="h6" component="h2">
                Редактирование данных аккаунта
            </Typography>
            <form style={{ display: 'flex', flexDirection: 'column', gap: 16 }} onSubmit={handleEditSubmit}>
                <Grid item xs={12} sx={{ marginBottom: 1 }}>
                    <Typography id="modal-modal-description" sx={{ mt: 1, marginBottom: 1 }}>
                        Введите ключ из письма для миграции на новую версию.
                    </Typography>
                    <TextField
                        fullWidth
                        label="Ключ"
                        value={editData.key}
                        onChange={(e) => handleInputChange('key', e.target.value)}
                        error={!!errors.key}
                        helperText={errors.key}
                        inputProps={{ maxLength: 40 }}
                    />
                </Grid>
                <Typography id="modal-modal-description">
                    Проверьте и при необходимости измените данные для миграции
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Имя"
                            value={editData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                            error={!!errors.firstName}
                            helperText={errors.firstName}
                            inputProps={{ maxLength: 40 }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Фамилия"
                            value={editData.lastName}
                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                            error={!!errors.lastName}
                            helperText={errors.lastName}
                            inputProps={{ maxLength: 40 }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Должность"
                            value={editData.position}
                            onChange={(e) => handleInputChange('position', e.target.value)}
                            error={!!errors.position}
                            helperText={errors.position}
                            inputProps={{ maxLength: 40 }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Название рабочего пространства"
                            value={editData.workspaceName}
                            onChange={(e) => handleInputChange('workspaceName', e.target.value)}
                            error={!!errors.workspaceName}
                            helperText={errors.workspaceName}
                            inputProps={{ maxLength: 40 }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <PhoneInput
                            fullWidth
                            label="Телефон"
                            value={editData.phoneNumber}
                            onChange={(value) => handleInputChange('phoneNumber', value)}
                            error={!!errors.phoneNumber}
                            helperText={errors.phoneNumber}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Пароль"
                            type="password"
                            value={editData.password}
                            onChange={(e) => handleInputChange('password', e.target.value)}
                            error={!!errors.password}
                            helperText={errors.password}
                            inputProps={{ maxLength: 40 }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Подтверждение пароля"
                            type="password"
                            value={editData.passwordConfirm}
                            onChange={(e) => handleInputChange('passwordConfirm', e.target.value)}
                            error={!!errors.passwordConfirm}
                            helperText={errors.passwordConfirm}
                            inputProps={{ maxLength: 40 }}
                        />
                    </Grid>
                </Grid>

                {editData.users && editData.users.length > 0 && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Box>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Пользователи рабочего пространства
                            </Typography>
                            {errors.users && (
                                <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                                    {errors.users}
                                </Typography>
                            )}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={selectedUsers.length === editData.users.length}
                                        indeterminate={
                                            selectedUsers.length > 0 && selectedUsers.length < editData.users.length
                                        }
                                        onChange={(e) => handleSelectAllUsers(e.target.checked)}
                                    />
                                }
                                label="Выбрать всех"
                            />
                            <Box sx={{ mt: 1, maxHeight: 200, overflowY: 'auto' }}>
                                {editData.users.map((user, index) => (
                                    <FormControlLabel
                                        key={index}
                                        control={
                                            <Checkbox
                                                checked={selectedUsers.includes(user.email)}
                                                onChange={(e) => handleUserSelection(user.email, e.target.checked)}
                                            />
                                        }
                                        label={`${user.firstName} ${user.lastName} (${user.email})`}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </>
                )}
                <Button variant="contained" color="primary" type="submit" disabled={loading}>
                    {loading ? (
                        <CircularProgress size={25} sx={{ color: '#fff', margin: '2px 0' }} />
                    ) : (
                        <>
                            Перенести аккаунт
                            <ArrowForwardIcon sx={{ ml: 1, fontSize: 18, color: '#fff' }} />
                        </>
                    )}
                </Button>
            </form>
        </>
    );

    const renderSuccessScreen = () => (
        <>
            <Box sx={{ textAlign: 'center', py: 2 }}>
                <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" component="h2" sx={{ mb: 2, color: 'success.main' }}>
                    Миграция завершена успешно!
                </Typography>
                <Alert
                    severity="info"
                    sx={{
                        marginBottom: 3,
                        textAlign: 'left',
                        '& .MuiAlert-message': {
                            '& a': {
                                color: 'inherit',
                                textDecoration: 'underline',
                                fontWeight: 'bold',
                            },
                        },
                        fontWeight: 'bold',
                    }}>
                    Добавьте новую ссылку в избранное или зафиксируйте ее в браузере <br />{' '}
                    <a
                        href="https://web.qwep.ru/"
                        target="_blank"
                        style={{
                            textAlign: 'center',
                            display: 'block',
                            fontSize: 18,
                            fontWeight: 'bold',
                            margin: '10px 0',
                        }}
                        rel="noopener noreferrer">
                        web.qwep.ru
                    </a>
                    Нажмите кнопку "Ок" чтобы перейти на новую версию по адресу
                </Alert>
                <Button
                    variant="contained"
                    color="success"
                    onClick={() => {
                        handleClose();
                        reloadPage();
                    }}
                    sx={{ minWidth: 120 }}>
                    Ок
                </Button>
            </Box>
        </>
    );

    return (
        <Modal
            open={openModal}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description">
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 620,
                    maxHeight: '90vh',
                    backgroundColor: colors.bg,
                    padding: 3,
                    borderRadius: 3,
                    overflowY: 'auto',
                }}>
                {showSuccessScreen ? renderSuccessScreen() : showEditForm ? renderEditForm() : renderValidationForm()}
            </Box>
        </Modal>
    );
};
